/**
 * End Customer Monitoring API service
 *
 * Provides real-time monitoring data for End Customer including:
 * - Aggregated metrics (CPU, Memory, Disk, Network)
 * - Per-VM metrics from Proxmox
 * - Alerts based on thresholds
 */

import { apiClient } from './api'

// --- Types ---

export type MonitoringRealtimeMetrics = {
  cpu: number
  memory: number
  disk: number
  network_in: number
  network_out: number
  uptime: number
  total_vms: number
  active_vms: number
  alerts_count: number
}

export type MonitoringVMMetrics = {
  id: string
  name: string
  status: string
  cpu: number
  memory: number
  disk: number
  network_in: number
  network_out: number
  uptime: string
  alerts: number
}

export type MonitoringAlert = {
  id: string
  type: 'critical' | 'warning' | 'info'
  title: string
  description: string
  timestamp: string
  vm_name: string | null
  vm_id: string | null
  metric: string
  value: number
  threshold: number
  resolved: boolean
}

export type CustomerMonitoringResponse = {
  realtime_metrics: MonitoringRealtimeMetrics
  vm_metrics: MonitoringVMMetrics[]
  alerts: MonitoringAlert[]
  timestamp: string
}

// --- API Functions ---

/**
 * Get monitoring data for end customer
 */
export const getCustomerMonitoring = async (): Promise<CustomerMonitoringResponse> => {
  return apiClient.get('/api/v1/end-customer/monitoring')
}

// --- Helper Functions ---

/**
 * Get color classes for VM status
 */
export const getStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    running: 'bg-green-100 text-green-800',
    stopped: 'bg-red-100 text-red-800',
    creating: 'bg-yellow-100 text-yellow-800',
    starting: 'bg-yellow-100 text-yellow-800',
    stopping: 'bg-orange-100 text-orange-800',
    error: 'bg-red-100 text-red-800',
  }
  return colors[status] || 'bg-gray-100 text-gray-800'
}

/**
 * Get label for VM status in Portuguese
 */
export const getStatusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    running: 'Online',
    stopped: 'Parada',
    creating: 'Criando',
    starting: 'Iniciando',
    stopping: 'Parando',
    error: 'Erro',
    terminated: 'Terminada',
  }
  return labels[status] || status
}

/**
 * Get color for alert type
 */
export const getAlertTypeColor = (type: string): string => {
  const colors: Record<string, string> = {
    critical: 'bg-red-100 text-red-800 border-red-200',
    warning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    info: 'bg-blue-100 text-blue-800 border-blue-200',
  }
  return colors[type] || 'bg-gray-100 text-gray-800 border-gray-200'
}

/**
 * Get label for alert type in Portuguese
 */
export const getAlertTypeLabel = (type: string): string => {
  const labels: Record<string, string> = {
    critical: 'Crítico',
    warning: 'Aviso',
    info: 'Info',
  }
  return labels[type] || type
}

/**
 * Get icon color for metric usage
 */
export const getMetricColor = (value: number): string => {
  if (value >= 90) return 'text-red-600'
  if (value >= 75) return 'text-yellow-600'
  return 'text-green-600'
}

/**
 * Get progress bar color for metric usage
 */
export const getProgressBarColor = (value: number): string => {
  if (value >= 90) return 'bg-red-600'
  if (value >= 75) return 'bg-yellow-600'
  return 'bg-green-600'
}

/**
 * Format network value with appropriate unit
 */
export const formatNetworkValue = (mbps: number): string => {
  if (mbps >= 1000) {
    return `${(mbps / 1000).toFixed(2)} GB/s`
  }
  return `${mbps.toFixed(2)} MB/s`
}

/**
 * Format relative time
 */
export const formatRelativeTime = (timestamp: string): string => {
  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60_000)
  const diffHours = Math.floor(diffMs / 3_600_000)
  const diffDays = Math.floor(diffMs / 86_400_000)

  if (diffMins < 1) return 'Agora mesmo'
  if (diffMins < 60) return `${diffMins} min atrás`
  if (diffHours < 24) return `${diffHours}h atrás`
  if (diffDays < 7) return `${diffDays} dias atrás`

  return date.toLocaleDateString('pt-BR')
}

/**
 * Format timestamp to locale string
 */
export const formatTimestamp = (timestamp: string): string => {
  return new Date(timestamp).toLocaleString('pt-BR')
}

// --- Export ---

const endCustomerMonitoringApi = {
  getCustomerMonitoring,
  getStatusColor,
  getStatusLabel,
  getAlertTypeColor,
  getAlertTypeLabel,
  getMetricColor,
  getProgressBarColor,
  formatNetworkValue,
  formatRelativeTime,
  formatTimestamp,
}

export default endCustomerMonitoringApi
